import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export interface ZohoTicket {
    id: string;
    ticketNumber: string;
    subject: string;
    status: string;
    description?: string;
    category?: string; // Added category
    createdTime: string;
    contactId?: string; // Add contactId
    email?: string;     // Add email field on ticket itself
    contact?: {
        id?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    };
}

export interface ZohoContact {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    secondaryEmail?: string;
}

export interface ZohoThread {
    id: string;
    content: string;
    createdTime: string;
    isDescriptionThread?: boolean;
    author?: {
        name: string;
        type: string;
    };
}

@Injectable({
    providedIn: 'root'
})
export class ZohoSupportService {
    private readonly clientId = '1000.HS0TAQYN7V40Q6MSPPA23BK0UBPKCI';
    private readonly clientSecret = '2e5ef5f7f12924bc18c3fef237f73d999be8cfc553';
    private fallbackRefreshToken = '1000.1d5ce5e8c3fcccf4f71e7fae7a7171da.f234a042e3c179978134d8c75c79e3f4';
    private zohoRefreshToken = this.fallbackRefreshToken;
    private readonly zohoOauthUrl = 'https://accounts.zoho.in/oauth/v2/token';
    private readonly zohoDeskUrl = 'https://desk.zoho.in/api/v1';
    private deskApiBaseUrl = this.zohoDeskUrl;
    private oauthBaseUrl = this.zohoOauthUrl;
    private readonly departmentId = '242494000000010772';
    private readonly zohoOrgId = '60062115220';

    private accessToken: string | null = null;
    private readonly STORAGE_KEY = 'zoho_token';
    private readonly REFRESH_STORAGE_KEY = 'zoho_refresh_token';

    constructor(private http: HttpClient) {
        this.accessToken = localStorage.getItem(this.STORAGE_KEY);
        const storedRefresh = localStorage.getItem(this.REFRESH_STORAGE_KEY);
        if (storedRefresh) {
            this.zohoRefreshToken = storedRefresh;
        } else {
            localStorage.setItem(this.REFRESH_STORAGE_KEY, this.zohoRefreshToken);
        }
        this.detectEnvironment();
    }

    private detectEnvironment(): void {
        const hasApi = !!(window as any).api;
        const hasProxy = hasApi && !!(window as any).api.zohoRequest;

        // Use local proxy if not in Electron
        // Use local proxy if not in Electron (Std)
        if (!hasProxy && !hasApi) {
            console.log('ZohoSupportService: Using local proxy on port 8010');
            this.oauthBaseUrl = 'http://localhost:8010/oauth/v2/token';
            this.deskApiBaseUrl = 'http://localhost:8010/api/v1';
        }
    }

    /**
     * Refresh the OAuth2 access token
     */
    private refreshAccessToken(): Observable<string> {
        console.log('ZohoSupportService: Refreshing token using refresh_token:', this.zohoRefreshToken.substring(0, 10) + '...');
        const body = new HttpParams()
            .set('refresh_token', this.zohoRefreshToken)
            .set('client_id', this.clientId)
            .set('client_secret', this.clientSecret)
            .set('grant_type', 'refresh_token');

        const hasApi = !!(window as any).api;
        const hasProxy = hasApi && !!(window as any).api.zohoRequest;

        if (hasProxy) {
            console.log('Refreshing Zoho Access Token (Proxy Implementation)...', { url: this.oauthBaseUrl });
            return from((window as any).api.zohoRequest({
                url: this.oauthBaseUrl,
                method: 'POST',
                body: body.toString(),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            })).pipe(
                map((res: any) => {
                    console.log('Zoho Token Refresh Proxy Response:', res);
                    if (res.ok && res.data && res.data.access_token) {
                        const newToken = res.data.access_token;
                        this.accessToken = newToken;
                        localStorage.setItem(this.STORAGE_KEY, newToken);
                        if (res.data.api_domain) {
                            console.log('Zoho API Domain (Proxy):', res.data.api_domain);
                        }
                        return newToken;
                    }
                    console.error('Zoho Token Refresh Proxy Failed. Response:', res);
                    throw new Error('Failed to refresh Zoho token (Proxy): ' + (res.data ? JSON.stringify(res.data) : res.statusText));
                }),
                catchError((err: any) => {
                    console.error('Zoho Token Refresh Proxy Error:', err);
                    return throwError(() => err);
                })
            );
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        return this.http.post<any>(this.oauthBaseUrl, body.toString(), { headers }).pipe(
            tap((res: any) => console.log('Zoho Token Refresh Response (HttpClient):', res)),
            map((res: any) => {
                if (res.access_token) {
                    const newToken = res.access_token;
                    this.accessToken = newToken;
                    localStorage.setItem(this.STORAGE_KEY, newToken);
                    if (res.api_domain) {
                        console.log('Zoho API Domain (HttpClient):', res.api_domain);
                    }
                    return newToken;
                }
                throw new Error('Failed to refresh Zoho token: ' + JSON.stringify(res));
            }),
            catchError((err: any) => {
                const errorMsg = err.error ? JSON.stringify(err.error) : err.message;
                const isCORS = err.status === 0 || err.message?.toLowerCase().includes('cors');
                const detailedError = isCORS
                    ? 'CORS Error: Zoho API does not allow browser-based requests. Use Electron app or a proxy.'
                    : errorMsg;
                return throwError(() => new Error(`Zoho Token Refresh Error: ${detailedError}`));
            })
        );
    }

    /**
     * Helper to ensure valid access token and prepare headers
     */
    private getAuthHeaders(forceRefresh = false): Observable<HttpHeaders> {
        if (forceRefresh) {
            this.accessToken = null;
            localStorage.removeItem(this.STORAGE_KEY);
        }

        // Always try to get most recent from storage
        this.accessToken = localStorage.getItem(this.STORAGE_KEY);

        const obs = this.accessToken ? of(this.accessToken) : this.refreshAccessToken();
        return obs.pipe(
            map((token: any) => {
                if (!token) {
                    console.error('ZohoSupportService: No access token available even after refresh attempts.');
                }
                return new HttpHeaders({
                    'Authorization': `Zoho-oauthtoken ${token}`,
                    'orgId': this.zohoOrgId,
                    'Content-Type': 'application/json'
                });
            })
        );
    }

    /**
     * Common handler for 401 errors to refresh token and retry
     */
    private requestWithRetry<T>(requestFn: (headers: HttpHeaders) => Observable<T>, skipOrgId: boolean = false): Observable<T> {
        return this.getAuthHeaders().pipe(
            switchMap((headers: any) => {
                let finalHeaders = headers;
                if (skipOrgId) {
                    finalHeaders = headers.delete('orgId').delete('orgid');
                }
                console.log(`[ZohoSupportService] Sending request to: ${requestFn.toString().substring(0, 100)}...`);
                console.log(`[ZohoSupportService] Headers:`, finalHeaders.keys());

                return requestFn(finalHeaders).pipe(
                    catchError((err: any) => {
                        console.log('Zoho request error intercepted:', {
                            status: err.status,
                            url: err.url,
                            body: err.error,
                            message: err.message
                        });
                        if (err.status === 401) {
                            console.warn('Zoho API returned 401. This means the access token is invalid or expired. Attempting refresh...');
                            return this.refreshAccessToken().pipe(
                                switchMap((newToken: any) => {
                                    console.log('Zoho token refreshed successfully, retrying original request...');
                                    let retryHeaders = new HttpHeaders({
                                        'Authorization': `Zoho-oauthtoken ${newToken}`,
                                        'Content-Type': 'application/json'
                                    });
                                    if (!skipOrgId) {
                                        retryHeaders = retryHeaders.set('orgId', this.zohoOrgId);
                                    }

                                    console.log(`[ZohoSupportService] Retrying with new token. Headers:`, retryHeaders.keys());
                                    return requestFn(retryHeaders);
                                }),
                                catchError((refreshErr: any) => {
                                    console.error('Zoho token refresh FAILED during retry. The refresh token might be invalid or expired.', refreshErr);
                                    return throwError(() => refreshErr);
                                })
                            );
                        }
                        return throwError(() => err);
                    })
                );
            })
        );
    }

    /**
     * Create a new ticket
     */
    createTicket(data: {
        subject: string;
        description: string;
        category?: string; // Added category
        contact: {
            firstName: string;
            lastName: string;
            email: string
        };
        contactId?: string;
        cf?: any
    }): Observable<any> {
        return this.requestWithRetry(headers => {
            const payload: any = {
                subject: data.subject,
                departmentId: this.departmentId,
                description: data.description,
                category: data.category, // Added category
                status: 'Open',
                cf: data.cf || {
                    cf_role: 'User',
                    cf_parking_id: '',
                    cf_org_id: '',
                    cf_app_source: 'Sistem',
                    cf_module: 'Support'
                }
            };

            if (data.contactId) {
                payload.contactId = data.contactId;
            } else {
                payload.contact = {
                    firstName: data.contact.firstName,
                    lastName: data.contact.lastName,
                    email: data.contact.email
                };
            }

            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: `${this.deskApiBaseUrl}/tickets`,
                    method: 'POST',
                    body: JSON.stringify(payload),
                    headers: headObj
                }));
            }
            return this.http.post(`${this.deskApiBaseUrl}/tickets`, payload, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        });
    }

    /**
     * Search contact by email
     */
    searchContactByEmail(email: string): Observable<ZohoContact | null> {
        return this.requestWithRetry(headers => {
            const url = `${this.deskApiBaseUrl}/contacts/search?email=${email}`;
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: headObj
                }));
            }
            return this.http.get<any>(url, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => {
                const data = res.data?.data;
                if (data && Array.isArray(data) && data.length > 0) {
                    return data[0] as ZohoContact;
                }
                return null;
            }),
            catchError((err: any) => {
                console.error('Zoho searchContactByEmail Error:', err);
                return of(null);
            })
        );
    }

    /**
     * Get contact details by ID
     */
    getContact(contactId: string): Observable<ZohoContact> {
        return this.requestWithRetry(headers => {
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            // Appending include=owner as suggested by user
            const url = `${this.deskApiBaseUrl}/contacts/${contactId}?include=owner`;

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: headObj
                }));
            }
            return this.http.get<any>(url, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => res.data as ZohoContact)
        );
    }

    /**
     * Create a new contact
     */
    createContact(contact: { firstName: string; lastName: string; email: string }): Observable<ZohoContact> {
        return this.requestWithRetry(headers => {
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: `${this.deskApiBaseUrl}/contacts`,
                    method: 'POST',
                    body: JSON.stringify(contact),
                    headers: headObj
                }));
            }
            return this.http.post<any>(`${this.deskApiBaseUrl}/contacts`, contact, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => res.data as ZohoContact)
        );
    }

    /**
     * List tickets
     */
    getTickets(email?: string): Observable<ZohoTicket[]> {
        console.log('Fetching Zoho tickets via Proxy...', { email });
        return this.requestWithRetry(headers => {
            // Use standard /tickets endpoint - works with Desk.tickets.READ scope
            const url = `${this.deskApiBaseUrl}/tickets`;

            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: headObj
                }));
            }
            // Fallback to HttpClient
            return this.http.get<any>(url, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            tap((res: any) => console.log('Zoho Tickets Response Raw:', res)),
            map((res: any) => {
                const data = res.data;
                if (data && data.data) {
                    return data.data as ZohoTicket[];
                }
                console.warn('Zoho Tickets: No "data" field in response', data);
                return (data && Array.isArray(data) ? data : []) as ZohoTicket[];
            }),
            catchError((err: any) => {
                console.error('Zoho getTickets Error:', err);
                return throwError(() => err);
            })
        );
    }

    /**
     * Get ticket details
     */
    getTicket(ticketId: string): Observable<ZohoTicket> {
        return this.requestWithRetry(headers => {
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: `${this.deskApiBaseUrl}/tickets/${ticketId}`,
                    method: 'GET',
                    headers: headObj
                }));
            }
            return this.http.get<any>(`${this.deskApiBaseUrl}/tickets/${ticketId}`, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => res.data as ZohoTicket)
        );
    }

    /**
     * Get ticket threads (conservations/messages) using /conversations endpoint
     */
    getTicketConversations(ticketId: string): Observable<ZohoThread[]> {
        return this.requestWithRetry(headers => {
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: `${this.deskApiBaseUrl}/tickets/${ticketId}/conversations`,
                    method: 'GET',
                    headers: headObj
                }));
            }
            return this.http.get<any>(`${this.deskApiBaseUrl}/tickets/${ticketId}/conversations`, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => {
                const data = res.data?.data;
                if (data && Array.isArray(data)) {
                    return data.map((item: any) => ({
                        id: item.id,
                        content: item.content || item.summary || 'No content',
                        createdTime: item.createdTime,
                        isDescriptionThread: item.isDescriptionThread,
                        author: {
                            name: item.author?.name || 'Unknown',
                            type: item.author?.type || 'User'
                        }
                    })) as ZohoThread[];
                }
                return [];
            })
        );
    }

    /**
     * Get individual thread details
     */
    getThread(threadId: string): Observable<any> {
        return this.requestWithRetry(headers => {
            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            // Note: Endpoint might be /threads/{id} or /tickets/{ticketId}/threads/{id}
            // Usually /threads/{id} works globally or dependent on context
            // Checking standard Zoho Desk API: GET /api/v1/threads/{threadId}
            // Request content, plainContent and htmlContent to be safe
            const url = `${this.deskApiBaseUrl}/threads/${threadId}?include=content,plainContent,htmlContent`;

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: headObj
                }));
            }
            return this.http.get<any>(url, { headers }).pipe(
                tap((res: any) => console.log(`[ZohoSupportService] Fetched Thread ${threadId} Details:`, res)),
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get ticket threads (conversations/messages)
     * @deprecated Use getTicketConversations instead
     */
    getTicketThreads(ticketId: string): Observable<ZohoThread[]> {
        return this.getTicketConversations(ticketId);
    }


    /**
     * Upload a file directly to the ticket (Step 1 of Reply with Attachment)
     * Endpoint: POST /tickets/{ticketId}/attachments
     * Scope: Desk.tickets.UPDATE (Likely available)
     */
    addAttachmentToTicket(ticketId: string, file: File): Observable<string> {
        return this.requestWithRetry(headers => {
            const formData = new FormData();
            formData.append('file', file);

            const headObj: any = {};
            headers.keys().forEach((k: any) => {
                if (k.toLowerCase() !== 'content-type') {
                    headObj[k] = headers.get(k);
                }
            });

            // Add orgId header
            if (this.zohoOrgId) {
                headObj['orgId'] = this.zohoOrgId;
            }

            const finalHeaders = new HttpHeaders(headObj);

            // Use the Ticket Attachment API as requested
            const orgId = this.zohoOrgId;
            const url = `${this.deskApiBaseUrl}/tickets/${ticketId}/attachments` + (orgId ? `?orgId=${orgId}` : '');



            if ((window as any).api && (window as any).api.zohoRequest) {
                return this.http.post(url, formData, { headers: finalHeaders }).pipe(
                    map((res: any) => ({ ok: true, data: res }))
                );
            }
            return this.http.post<any>(url, formData, { headers: finalHeaders }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }).pipe(
            map((res: any) => {
                const id = res.data?.id;
                if (id) return id;
                console.error('Attachment response invalid:', res);
                throw new Error('Attachment failed: No ID returned. ' + JSON.stringify(res));
            }),
            catchError((err: any) => {
                console.error('Attachment Observable Error:', err);
                return throwError(() => err);
            })
        );
    }


    /**
     * Reply to a ticket (Adds a thread/comment from the user via impersonation)
     */
    replyToTicket(ticketId: string, content: string, fromEmail: string, contactId: string, attachmentIds: string[] = []): Observable<any> {
        return this.requestWithRetry(headers => {
            const payload: any = {
                channel: "EMAIL",
                to: "support@kormoanpvtltd.zohodesk.in",
                fromEmailAddress: fromEmail,
                contentType: "html",
                content: content
            };

            if (attachmentIds && attachmentIds.length > 0) {
                payload.attachmentIds = attachmentIds;
            }



            const headObj: any = {};
            headers.keys().forEach((k: any) => headObj[k] = headers.get(k));

            // Add impersonation header
            if (contactId) {
                headObj['impersonatedUserId'] = contactId;
            }
            // Add orgId header
            if (this.zohoOrgId) {
                headObj['orgId'] = this.zohoOrgId;
            }

            const finalHeaders = new HttpHeaders(headObj);

            // Append orgId query param just in case
            const orgId = this.zohoOrgId;
            const url = `${this.deskApiBaseUrl}/tickets/${ticketId}/sendReply` + (orgId ? `?orgId=${orgId}` : '');

            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url.split('?')[0],
                    method: 'POST',
                    body: JSON.stringify(payload),
                    headers: headObj
                }));
            }
            return this.http.post(url, payload, { headers: finalHeaders }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        });
    }

    /**
     * Get organizations (useful for finding the correct orgId)
     */
    getOrganizations(): Observable<any> {
        return this.requestWithRetry(headers => {
            const url = `${this.deskApiBaseUrl}/organizations`;
            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: this.headersToObj(headers)
                }));
            }
            return this.http.get(url, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }, true);
    }

    /**
     * Get departments (can sometimes reveal org info)
     */
    getDepartments(): Observable<any> {
        return this.requestWithRetry(headers => {
            const url = `${this.deskApiBaseUrl}/departments`;
            if ((window as any).api && (window as any).api.zohoRequest) {
                return from((window as any).api.zohoRequest({
                    url: url,
                    method: 'GET',
                    headers: this.headersToObj(headers)
                }));
            }
            return this.http.get(url, { headers }).pipe(
                map((res: any) => ({ ok: true, data: res }))
            );
        }, false); // Try WITH orgId for departments
    }

    private headersToObj(headers: HttpHeaders): any {
        const obj: any = {};
        headers.keys().forEach((k: any) => obj[k] = headers.get(k));
        return obj;
    }
}
